/**
 * Axios client with JWT bearer token injection and 401 refresh logic.
 */
import axios, { type AxiosError } from 'axios';
import { useAuthStore } from '@/stores/authStore';

// Always use relative URLs so the Vite dev-server proxy (or nginx in prod)
// forwards /api → http://api:8000. Using VITE_API_URL as a base URL causes
// the browser to try to connect to the Docker-internal hostname directly,
// which fails because it's only resolvable inside the Docker network.
const API_BASE = '';

export const apiClient = axios.create({
  baseURL: `${API_BASE}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

// ── Request interceptor — attach access token ────────────────────────────
apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── Response interceptor — handle 401 → token refresh ───────────────────
let isRefreshing = false;
let refreshQueue: Array<(token: string) => void> = [];

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as typeof error.config & { _retry?: boolean };

    if (error.response?.status !== 401 || original?._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve) => {
        refreshQueue.push((token) => {
          if (original) original.headers!.Authorization = `Bearer ${token}`;
          resolve(apiClient(original!));
        });
      });
    }

    original!._retry = true;
    isRefreshing = true;

    try {
      const { refreshToken, setTokens } = useAuthStore.getState();
      if (!refreshToken) throw new Error('No refresh token');

      const { data } = await axios.post(`/api/v1/auth/refresh`, {
        refresh_token: refreshToken,
      });

      setTokens(data.access_token, data.refresh_token);
      refreshQueue.forEach((cb) => cb(data.access_token));
      refreshQueue = [];

      original!.headers!.Authorization = `Bearer ${data.access_token}`;
      return apiClient(original!);
    } catch {
      useAuthStore.getState().logout();
      window.location.href = '/login';
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  },
);

export default apiClient;
